import { useEffect, useState } from "react";
import { useLang } from "../../../i18n/LanguageContext";
import {
  PROFILE_CONTINUITY_CHANGE_EVENT,
  getProfileContinuityStatus,
} from "../../dreams/utils/profileContinuity";
import {
  deleteProfileContinuityRest,
  getReadableProfileContinuityRest,
  saveProfileContinuityConsentRest,
  syncProfileContinuityFromRest,
} from "../../dreams/utils/profileContinuityRest";

const getTraceCopy = (lang) => ({
  locale: lang === "en" ? "en-US" : "pl-PL",
  enabled: lang === "en" ? "enabled" : "włączone",
  disabled: lang === "en" ? "disabled" : "wyłączone",
  yes: lang === "en" ? "yes" : "tak",
  no: lang === "en" ? "no" : "nie",
  saving: lang === "en" ? "Saving…" : "Zapisuję…",
  cancel: lang === "en" ? "Cancel" : "Anuluj",
  modalEyebrow: lang === "en" ? "Dream trace" : "Ślad snów",
  modal: {
    build_on: {
      title: lang === "en" ? "Build dream trace" : "Buduj Ślad snów",
      text: lang === "en"
        ? "After a successful analysis, Morpheus may remember only an encrypted trace of motifs, emotions and symbols. The full dream body is not stored as a trace."
        : "Morfeusz może po udanej analizie zapamiętywać tylko zaszyfrowany ślad motywów, emocji i symboli. Pełna treść snu nie jest zapisywana jako Ślad snów.",
      effect: lang === "en"
        ? "After enabling this, future analyses may add new traces."
        : "Po włączeniu kolejne analizy mogą dopisywać nowe ślady.",
      confirm: lang === "en" ? "I understand and enable" : "Rozumiem i włączam",
    },
    build_off: {
      title: lang === "en" ? "Disable trace building" : "Wyłącz budowanie Śladu snów",
      text: lang === "en"
        ? "Morpheus will stop adding new traces of motifs, emotions and symbols after future analyses."
        : "Morfeusz przestanie dopisywać nowe ślady motywów, emocji i symboli po kolejnych analizach.",
      effect: lang === "en"
        ? "Disabling trace building also disables using it in AI analysis."
        : "Wyłączenie budowania Śladu snów wyłączy też użycie go w analizie AI.",
      confirm: lang === "en" ? "I understand and disable" : "Rozumiem i wyłączam",
    },
    ai_on: {
      title: lang === "en" ? "Use dream trace in AI analysis" : "Używaj Śladu snów w analizie AI",
      text: lang === "en"
        ? "Morpheus may use the dream trace as context in the “Against your history” section."
        : "Morfeusz może korzystać ze Śladu snów jako kontekstu w sekcji „Na tle historii”.",
      effect: lang === "en"
        ? "This consent does not store the full dream body. It only allows using the encrypted trace if it exists."
        : "Ta zgoda nie zapisuje pełnej treści snów. Pozwala tylko użyć zaszyfrowanego Śladu snów, jeśli taki ślad istnieje.",
      confirm: lang === "en" ? "I understand and enable" : "Rozumiem i włączam",
    },
    ai_off: {
      title: lang === "en" ? "Disable trace use in AI analysis" : "Wyłącz użycie Śladu snów w analizie AI",
      text: lang === "en"
        ? "Morpheus will stop using the dream trace as context in future analyses."
        : "Morfeusz przestanie używać Śladu snów jako kontekstu przy kolejnych analizach.",
      effect: lang === "en"
        ? "Trace data may still exist if trace building remains enabled, but AI analysis will not use it."
        : "Dane Śladu snów mogą nadal istnieć, jeśli budowanie pozostaje włączone, ale analiza AI nie będzie z nich korzystać.",
      confirm: lang === "en" ? "I understand and disable" : "Rozumiem i wyłączam",
    },
    delete_profile: {
      title: lang === "en" ? "Delete dream trace data" : "Usuń dane Śladu snów",
      text: lang === "en"
        ? "Morpheus will delete the encrypted trace pack and disable both consents."
        : "Morfeusz usunie zaszyfrowany pack Śladu snów i wyłączy obie zgody.",
      effect: lang === "en"
        ? "Dreams remain unchanged. Only the dream trace and its settings are removed."
        : "Sny pozostaną bez zmian. Usuwamy tylko Ślad snów i jego ustawienia.",
      confirm: lang === "en" ? "I understand and delete" : "Rozumiem i usuwam",
    },
  },
  readableEyebrow: lang === "en" ? "My trace data" : "Moje dane Śladu snów",
  summary: lang === "en" ? "Summary" : "Podsumowanie",
  motifs: lang === "en" ? "Motifs" : "Motywy",
  emotions: lang === "en" ? "Emotions" : "Emocje",
  samples: lang === "en" ? "Samples" : "Próbki",
  recurringMotifs: lang === "en" ? "Recurring motifs" : "Powracające motywy",
  recurringEmotions: lang === "en" ? "Recurring emotions" : "Powracające emocje",
  recentTraces: lang === "en" ? "Recent traces" : "Ostatnie ślady",
  dreamFallback: lang === "en" ? "Dream" : "Sen",
  pageEyebrow: lang === "en" ? "Dream trace" : "Ślad snów",
  titlePre: lang === "en" ? "Dream" : "Ślad",
  titleAccent: lang === "en" ? "trace" : "snów",
  subtitle: lang === "en"
    ? "A separate encrypted extract of recurring motifs, emotions and symbols. The full dream body is not stored as a trace."
    : "Osobny, zaszyfrowany wyciąg z powracających motywów, emocji i symboli. Pełna treść snu nie jest zapisywana jako Ślad snów.",
  buildStat: lang === "en" ? "Trace building" : "Budowanie śladu",
  aiStat: lang === "en" ? "Use in AI analysis" : "Użycie w analizie AI",
  packStat: lang === "en" ? "Trace data" : "Dane śladu",
  updatedStat: lang === "en" ? "Last update" : "Ostatnia aktualizacja",
  savedNotice: lang === "en" ? "Dream trace settings saved." : "Zapisano ustawienia Śladu snów.",
  saveFailed: lang === "en" ? "Could not save dream trace settings." : "Nie udało się zapisać ustawień Śladu snów.",
  defaultOffNotice: lang === "en"
    ? "The dream trace is disabled by default. Enable it only if you want Morpheus to remember an encrypted trace of motifs, emotions and symbols."
    : "Ślad snów jest domyślnie wyłączony. Włącz go tylko wtedy, gdy chcesz, aby Morfeusz zapamiętywał zaszyfrowany ślad motywów, emocji i symboli.",
  buildTitle: lang === "en" ? "Build trace" : "Buduj Ślad snów",
  buildText: lang === "en"
    ? "After analysis, remember only an encrypted trace of motifs, emotions and symbols."
    : "Po analizie zapamiętaj tylko zaszyfrowany ślad motywów, emocji i symboli.",
  aiTitle: lang === "en" ? "Use in AI analysis" : "Używaj w analizie AI",
  aiText: lang === "en"
    ? "Let Morpheus use the dream trace in the “Against your history” section."
    : "Pozwól Morfeuszowi korzystać ze Śladu snów w sekcji „Na tle historii”.",
  on: lang === "en" ? "Enabled" : "Włączone",
  enable: lang === "en" ? "Enable" : "Włącz",
  disable: lang === "en" ? "Disable" : "Wyłącz",
  showData: lang === "en" ? "Show my data" : "Pokaż moje dane",
  exportProfile: lang === "en" ? "Export trace" : "Eksportuj Ślad snów",
  exportStarted: lang === "en" ? "Preparing trace export…" : "Przygotowuję eksport Śladu snów…",
  exportDone: lang === "en" ? "Dream trace exported locally." : "Wyeksportowano Ślad snów lokalnie.",
  exportFailed: lang === "en" ? "Could not export trace data." : "Nie udało się wyeksportować danych Śladu snów.",
  deleteProfile: lang === "en" ? "Delete dream trace" : "Usuń Ślad snów",
  backRoom: lang === "en" ? "Back to Room" : "Wróć do Pokoiku",
  openingData: lang === "en" ? "Opening trace data…" : "Otwieram dane Śladu snów…",
  decryptedLocal: lang === "en"
    ? "Trace data has been decrypted locally in the browser."
    : "Dane Śladu snów zostały odszyfrowane lokalnie w przeglądarce.",
  noProfileData: lang === "en" ? "The trace has no saved data yet." : "Ślad snów nie ma jeszcze zapisanych danych.",
  showFailed: lang === "en" ? "Could not show trace data." : "Nie udało się pokazać danych Śladu snów.",
});

const formatDate = (value, copy) => {
  if (!value) return "—";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString(copy.locale, { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch (_) {
    return "—";
  }
};

const statusText = (value, copy) => (value ? copy.enabled : copy.disabled);
const yesNo = (value, copy) => (value ? copy.yes : copy.no);
const topEntries = (map, limit = 8) => Object.entries(map || {}).map(([key, score]) => ({ key, score: Number(score || 0) })).filter((item) => item.key && item.score > 0).sort((a, b) => b.score - a.score).slice(0, limit);

const downloadJson = (filename, payload) => {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const exportFileName = () => {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  return `morfeusz-slad-snow-${stamp}.json`;
};

const ConsentModal = ({ type, busy, copy, onCancel, onConfirm }) => {
  const modalCopy = copy.modal[type] || copy.modal.build_on;
  return <div className="mdDrawer__backdrop" role="dialog" aria-modal="true" data-ui="profile-consent-modal" onClick={busy ? undefined : onCancel}><div className="mdCard" data-ui="profile-consent-card" onClick={(event) => event.stopPropagation()}><p className="mdPage__eyebrow">{copy.modalEyebrow}</p><h2 className="mdPage__title">{modalCopy.title}</h2><p className="mdPage__subtitle">{modalCopy.text}</p><p data-ui="profile-consent-effect">{modalCopy.effect}</p><div className="mdRow" data-ui="profile-consent-modal-actions"><button type="button" className="mdBtn mdBtn--primary mdBtn--row" onClick={onConfirm} disabled={busy} data-ui="profile-consent-confirm">{busy ? copy.saving : modalCopy.confirm}</button><button type="button" className="mdBtn mdBtn--row" onClick={onCancel} disabled={busy} data-ui="profile-consent-cancel">{copy.cancel}</button></div></div></div>;
};

const ProfileDataPreview = ({ profile, copy }) => {
  if (!profile) return null;
  const motifs = topEntries(profile.motifs, 10);
  const emotions = topEntries(profile.emotions, 8);
  const samples = Array.isArray(profile.samples) ? profile.samples.slice(0, 8) : [];
  return <div className="mdCard" data-ui="profile-readable-data"><div className="mdCard__header"><p className="mdPage__eyebrow">{copy.readableEyebrow}</p></div>{profile.tldr && <div data-ui="profile-readable-section"><h3>{copy.summary}</h3><p className="mdPage__subtitle">{profile.tldr}</p></div>}<div className="mdProfileGrid" data-ui="profile-readable-grid"><div className="mdStat" data-ui="profile-readable-motifs"><div className="mdStat__num">{motifs.length}</div><div className="mdStat__label">{copy.motifs}</div></div><div className="mdStat" data-ui="profile-readable-emotions"><div className="mdStat__num">{emotions.length}</div><div className="mdStat__label">{copy.emotions}</div></div><div className="mdStat" data-ui="profile-readable-samples"><div className="mdStat__num">{samples.length}</div><div className="mdStat__label">{copy.samples}</div></div></div>{motifs.length > 0 && <div data-ui="profile-readable-section"><h3>{copy.recurringMotifs}</h3><p className="mdPage__subtitle">{motifs.map((item) => `${item.key} (${Math.round(item.score)})`).join(", ")}</p></div>}{emotions.length > 0 && <div data-ui="profile-readable-section"><h3>{copy.recurringEmotions}</h3><p className="mdPage__subtitle">{emotions.map((item) => `${item.key} (${Math.round(item.score)})`).join(", ")}</p></div>}{samples.length > 0 && <div data-ui="profile-readable-section"><h3>{copy.recentTraces}</h3><div data-ui="profile-readable-samples-list">{samples.map((sample, index) => <div className="mdAlert mdAlert--info" key={`${sample.dream_id || sample.title || "sample"}-${index}`} data-ui="profile-readable-sample"><strong>{sample.title || copy.dreamFallback}</strong>{sample.date ? ` · ${sample.date}` : ""}{sample.summary ? <div>{sample.summary}</div> : null}</div>)}</div></div>}</div>;
};

export const StrefaPage = () => {
  const { lang } = useLang();
  const copy = getTraceCopy(lang);
  const [status, setStatus] = useState(() => getProfileContinuityStatus());
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    let mounted = true;
    const sync = () => setStatus(getProfileContinuityStatus());
    const syncRemote = async () => {
      try {
        const next = await syncProfileContinuityFromRest();
        if (mounted) setStatus(next);
      } catch (_) {
        sync();
      }
    };
    syncRemote();
    window.addEventListener(PROFILE_CONTINUITY_CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    window.addEventListener("focus", syncRemote);
    return () => { mounted = false; window.removeEventListener(PROFILE_CONTINUITY_CHANGE_EVENT, sync); window.removeEventListener("storage", sync); window.removeEventListener("focus", syncRemote); };
  }, []);

  const confirmConsent = async () => {
    const type = modal;
    if (!type || busy) return;
    setBusy(true);
    setNotice("");
    try {
      let next;
      if (type === "build_on") next = await saveProfileContinuityConsentRest({ buildOptin: true, aiUseOptin: status.aiUseOptin });
      if (type === "build_off") next = await saveProfileContinuityConsentRest({ buildOptin: false, aiUseOptin: false });
      if (type === "ai_on") next = await saveProfileContinuityConsentRest({ buildOptin: true, aiUseOptin: true });
      if (type === "ai_off") next = await saveProfileContinuityConsentRest({ buildOptin: status.buildOptin, aiUseOptin: false });
      if (type === "delete_profile") { next = await deleteProfileContinuityRest(); setProfileData(null); }
      if (next) setStatus(next);
      setModal(null);
      setNotice(copy.savedNotice);
    } catch (error) {
      setNotice(error?.message || copy.saveFailed);
    } finally {
      setBusy(false);
    }
  };

  const showReadableProfile = async () => {
    if (busy) return;
    setBusy(true);
    setNotice(copy.openingData);
    try {
      const response = await getReadableProfileContinuityRest();
      setStatus(response.state || getProfileContinuityStatus());
      setProfileData(response.profile || null);
      setNotice(response.profile ? copy.decryptedLocal : copy.noProfileData);
    } catch (error) {
      setNotice(error?.message || copy.showFailed);
    } finally {
      setBusy(false);
    }
  };

  const exportReadableProfile = async () => {
    if (busy) return;
    setBusy(true);
    setNotice(copy.exportStarted);
    try {
      const response = await getReadableProfileContinuityRest();
      const nextStatus = response.state || getProfileContinuityStatus();
      const nextProfile = response.profile || null;
      setStatus(nextStatus);
      setProfileData(nextProfile);
      downloadJson(exportFileName(), { app: "Morfeusz Dreams", type: "dream_trace_export", version: 1, exportedAt: new Date().toISOString(), language: lang, state: nextStatus, profile: nextProfile });
      setNotice(nextProfile ? copy.exportDone : copy.noProfileData);
    } catch (error) {
      setNotice(error?.message || copy.exportFailed);
    } finally {
      setBusy(false);
    }
  };

  return <section className="mdPage" data-ui="strefa-root"><div className="mdPage__inner mdPage__inner--wide"><div><p className="mdPage__eyebrow">{copy.pageEyebrow}</p><h1 className="mdPage__title">{copy.titlePre} <em>{copy.titleAccent}</em></h1><p className="mdPage__subtitle">{copy.subtitle}</p></div><div className="mdCard" data-ui="profile-continuity-card"><div className="mdProfileGrid" data-ui="profile-continuity-stats"><div className="mdStat" data-ui="profile-continuity-build-stat"><div className="mdStat__num">{statusText(status.buildOptin, copy)}</div><div className="mdStat__label">{copy.buildStat}</div></div><div className="mdStat" data-ui="profile-continuity-ai-stat"><div className="mdStat__num">{statusText(status.aiUseOptin, copy)}</div><div className="mdStat__label">{copy.aiStat}</div></div><div className="mdStat" data-ui="profile-continuity-pack-stat"><div className="mdStat__num">{yesNo(status.hasPack, copy)}</div><div className="mdStat__label">{copy.packStat}</div></div><div className="mdStat" data-ui="profile-continuity-updated-stat"><div className="mdStat__num">{formatDate(status.updatedAt, copy)}</div><div className="mdStat__label">{copy.updatedStat}</div></div></div>{notice && <div className="mdAlert mdAlert--info" data-ui="profile-continuity-notice">{notice}</div>}{!status.explicitConsent && <div className="mdAlert mdAlert--info" data-ui="profile-continuity-consent-alert">{copy.defaultOffNotice}</div>}<div className="mdCard" data-ui="profile-continuity-build-card"><div className="mdCard__header"><h2>{copy.buildTitle}</h2></div><p className="mdPage__subtitle">{copy.buildText}</p><div className="mdRow" data-ui="profile-continuity-build-actions"><button type="button" className="mdBtn mdBtn--primary mdBtn--row" disabled={status.buildOptin || busy} onClick={() => setModal("build_on")}>{status.buildOptin ? copy.on : copy.enable}</button><button type="button" className="mdBtn mdBtn--row" disabled={!status.buildOptin || busy} onClick={() => setModal("build_off")}>{copy.disable}</button></div></div><div className="mdCard" data-ui="profile-continuity-ai-card"><div className="mdCard__header"><h2>{copy.aiTitle}</h2></div><p className="mdPage__subtitle">{copy.aiText}</p><div className="mdRow" data-ui="profile-continuity-ai-actions"><button type="button" className="mdBtn mdBtn--primary mdBtn--row" disabled={status.aiUseOptin || busy} onClick={() => setModal("ai_on")}>{status.aiUseOptin ? copy.on : copy.enable}</button><button type="button" className="mdBtn mdBtn--row" disabled={!status.aiUseOptin || busy} onClick={() => setModal("ai_off")}>{copy.disable}</button></div></div><div className="mdRow" data-ui="profile-continuity-actions"><button type="button" className="mdBtn mdBtn--primary mdBtn--row" disabled={busy || !status.hasPack} onClick={showReadableProfile}>{copy.showData}</button><button type="button" className="mdBtn mdBtn--row" disabled={busy || !status.hasPack} onClick={exportReadableProfile} data-ui="profile-continuity-export">{copy.exportProfile}</button><button type="button" className="mdBtn mdBtn--row" disabled={busy || (!status.hasPack && !status.buildOptin && !status.aiUseOptin)} onClick={() => setModal("delete_profile")}>{copy.deleteProfile}</button><a className="mdBtn mdBtn--row" href="#/pokoik">{copy.backRoom}</a></div></div><ProfileDataPreview profile={profileData} copy={copy} /></div>{modal && <ConsentModal type={modal} busy={busy} copy={copy} onCancel={() => setModal(null)} onConfirm={confirmConsent} />}</section>;
};
